import { createTheme, Theme } from "@mui/material";
import { KLYDO_BLACK, KLYDO_YELLOW } from "../../consts/colors";
import {
  LARGE_SCREEN,
  MEDIUM_SCREEN,
  MOBILE,
  SMALL_SCREEN,
  SMALL_TABLET,
  TABLET,
} from "../../consts/breakpoints";

export type ThemeMode = "light" | "dark";

declare module "@mui/material/styles" {
  interface Theme {
    dragAndDrop: {
      transition: string;
      draggedOpacity: number;
    };
  }

  interface ThemeOptions {
    dragAndDrop?: {
      transition?: string;
      draggedOpacity?: number;
    };
  }

  interface ZIndex {
    pendulumRod: number;
    pendulumBall: number;
    clockDials: number;
    handMinute: number;
    handMinuteS: number;
    handHour: number;
    handHourS: number;
    dialog: number;
  }

  interface BreakpointOverrides {
    xxs: true;
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }

  interface Palette {
    paper: {
      elevation2: string;
      elevation3: string;
      elevation4: string;
      elevation5: string;
      elevation6: string;
      elevation8: string;
      elevation10: string;
      elevation12: string;
      elevation24: string;
    };
    /**
     * Semantic overlay scale: subtle foreground overlays on the app
     * background. In dark mode these are white alphas, in light mode
     * black alphas — components using these tokens flip automatically.
     */
    white: {
      5: string;
      8: string;
      9: string;
      10: string;
      12: string;
      16: string;
      20: string;
      24: string;
      56: string;
      70: string;
      90: string;
    };
    black: {
      19: string;
      50: string;
      80: string;
    };
    gray: {
      119: string;
    };
    /** App surfaces that aren't expressible as a single palette color. */
    surface: {
      /** flat card background */
      card: string;
      /** section-card gradient used in record modals */
      cardGradient: string;
      /** floating panel / table container background */
      panel: string;
      /** translucent app-bar background (used with backdrop blur) */
      header: string;
      /** navigation drawer gradient */
      drawer: string;
      /** hairline border for cards/panels */
      border: string;
      /** brand accent usable as TEXT on the app background (yellow on dark, amber on light) */
      accentText: string;
    };
  }

  interface PaletteOptions {
    paper?: Palette["paper"];
    white?: Palette["white"];
    black?: Palette["black"];
    gray?: Palette["gray"];
    surface?: Palette["surface"];
  }

  interface TypographyVariants {
    label: React.CSSProperties;
    value: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
    value?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    label: true;
    value: true;
  }
}

const dark = {
  textPrimary: "rgba(255, 255, 255, 0.9)",
  textSecondary: "rgba(255, 255, 255, 0.7)",
  textMuted: "rgba(255, 255, 255, 0.65)",
};

const light = {
  textPrimary: "rgba(12, 12, 12, 0.92)",
  textSecondary: "rgba(12, 12, 12, 0.62)",
  textMuted: "rgba(12, 12, 12, 0.55)",
};

export const createAppTheme = (mode: ThemeMode): Theme => {
  const isDark = mode === "dark";
  const t = isDark ? dark : light;

  return createTheme({
    breakpoints: {
      values: {
        // xs stays 0 (MUI default) so legacy `xs={12}` Grid props keep
        // applying on phones; xxs is an alias base for studio-style sx.
        // Use the MOBILE const directly for a 530px media query.
        xxs: 0,
        xs: 0,
        sm: SMALL_TABLET,
        md: TABLET,
        lg: SMALL_SCREEN,
        xl: MEDIUM_SCREEN,
        xxl: LARGE_SCREEN,
      },
    },
    palette: {
      mode,
      action: isDark
        ? {
            disabled: "rgba(255, 255, 255, 0.12)",
            active: "rgba(255, 255, 255, 0.56)",
            disabledBackground: "rgba(255, 255, 255, 0.12)",
          }
        : {
            disabled: "rgba(0, 0, 0, 0.26)",
            active: "rgba(0, 0, 0, 0.54)",
            disabledBackground: "rgba(0, 0, 0, 0.12)",
          },
      primary: {
        main: KLYDO_YELLOW,
        contrastText: KLYDO_BLACK,
        light: "#FFDB57",
      },
      secondary: isDark
        ? {
            main: "#2D2D2D",
            contrastText: "rgba(255, 255, 255, 1)",
            light: "#494848",
            dark: "#1F1F1F",
          }
        : {
            main: "#E8E6E0",
            contrastText: "rgba(12, 12, 12, 0.92)",
            light: "#F2F0EA",
            dark: "#D6D3CB",
          },
      background: isDark
        ? {
            default: KLYDO_BLACK,
            paper: "#1A1A1A",
          }
        : {
            default: "#F6F5F1",
            paper: "#FFFFFF",
          },
      text: {
        primary: t.textPrimary,
        secondary: t.textSecondary,
      },
      paper: isDark
        ? {
            elevation2: "#232323",
            elevation3: "#252525",
            elevation4: "#272727",
            elevation5: "#2A2A2A",
            elevation6: "#2C2C2C",
            elevation8: "#2E2E2E",
            elevation10: "#313131",
            elevation12: "#333333",
            elevation24: "#383838",
          }
        : {
            elevation2: "#F7F6F2",
            elevation3: "#F5F4EF",
            elevation4: "#F2F1EC",
            elevation5: "#EFEEE8",
            elevation6: "#ECEBE5",
            elevation8: "#EAE8E2",
            elevation10: "#E7E5DF",
            elevation12: "#E4E2DC",
            elevation24: "#DEDCD5",
          },
      white: isDark
        ? {
            5: "rgba(255, 255, 255, 0.05)",
            8: "rgba(255, 255, 255, 0.08)",
            9: "rgba(255, 255, 255, 0.09)",
            10: "rgba(255, 255, 255, 0.1)",
            12: "rgba(255, 255, 255, 0.12)",
            16: "rgba(255, 255, 255, 0.16)",
            20: "rgba(255, 255, 255, 0.20)",
            24: "rgba(255, 255, 255, 0.24)",
            56: "rgba(255, 255, 255, 0.56)",
            70: "rgba(255, 255, 255, 0.70)",
            90: "rgba(255, 255, 255, 0.90)",
          }
        : {
            5: "rgba(0, 0, 0, 0.04)",
            8: "rgba(0, 0, 0, 0.05)",
            9: "rgba(0, 0, 0, 0.06)",
            10: "rgba(0, 0, 0, 0.07)",
            12: "rgba(0, 0, 0, 0.08)",
            16: "rgba(0, 0, 0, 0.11)",
            20: "rgba(0, 0, 0, 0.14)",
            24: "rgba(0, 0, 0, 0.17)",
            56: "rgba(0, 0, 0, 0.42)",
            70: "rgba(0, 0, 0, 0.55)",
            90: "rgba(0, 0, 0, 0.82)",
          },
      black: {
        19: "rgba(0, 0, 0, 0.19)",
        50: "rgba(0, 0, 0, 0.5)",
        80: "rgba(0, 0, 0, 0.8)",
      },
      gray: {
        119: "rgba(119, 119, 119, 1)",
      },
      surface: isDark
        ? {
            card: "#1A1A1A",
            cardGradient: "linear-gradient(180deg, #1F1F1F 0%, #181818 100%)",
            panel: "#141414",
            header: "rgba(12, 12, 12, 0.85)",
            drawer: "linear-gradient(0deg, #0C0C0C 0%, #121212 100%)",
            border: "rgba(255, 255, 255, 0.06)",
            accentText: "#FFD848",
          }
        : {
            card: "#FFFFFF",
            cardGradient: "linear-gradient(180deg, #FFFFFF 0%, #F7F6F2 100%)",
            panel: "#FFFFFF",
            header: "rgba(246, 245, 241, 0.85)",
            drawer: "linear-gradient(0deg, #FFFFFF 0%, #FAF9F6 100%)",
            border: "rgba(0, 0, 0, 0.08)",
            accentText: "#8A6D00",
          },
    },
    typography: {
      fontFamily: [
        "Gilroy-Medium",
        "Gilroy-Regular",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "sans-serif",
      ].join(","),
      button: {
        textTransform: "capitalize",
        fontFamily: "Gilroy-SemiBold",
        fontWeight: 500,
      },
      label: {
        fontFamily: "Gilroy-Medium",
        fontSize: "14px",
        lineHeight: "normal",
        fontWeight: 400,
        color: t.textMuted,
      },
      body1: {
        fontFamily: "Gilroy-Medium",
        fontSize: "16px",
        lineHeight: "150%",
        fontWeight: 400,
      },
      body2: {
        fontFamily: "Gilroy-Medium",
        fontSize: "14px",
        lineHeight: "143%",
        fontWeight: 400,
      },
      h1: {
        fontFamily: "Gilroy-Bold",
        lineHeight: "117%",
        fontSize: "96px",
        fontWeight: 300,
      },
      h2: {
        fontFamily: "Gilroy-Bold",
        lineHeight: "120%",
        fontSize: "60px",
        fontWeight: 300,
      },
      h3: {
        fontFamily: "Gilroy-Bold",
        lineHeight: "56px",
        fontSize: "48px",
        fontWeight: 400,
        color: isDark ? "rgba(255, 255, 255, 1)" : "rgba(12, 12, 12, 0.95)",
      },
      h4: {
        fontFamily: "Gilroy-SemiBold",
        fontSize: "34px",
        lineHeight: "42px",
        fontWeight: 400,
      },
      h5: {
        fontFamily: "Gilroy-SemiBold",
        fontSize: "24px",
        lineHeight: "32px",
        fontWeight: 400,
      },
      h6: {
        fontFamily: "Gilroy-SemiBold",
        fontSize: "19px",
        lineHeight: "120%",
        fontWeight: 500,
      },
      subtitle1: {
        fontFamily: "Gilroy-Bold",
        fontSize: "18px",
        lineHeight: "120%",
        fontWeight: 400,
      },
      subtitle2: {
        fontFamily: "Gilroy-SemiBold",
        fontSize: "14px",
        lineHeight: "157%",
        fontWeight: 500,
      },
      value: {
        fontFamily: "Gilroy-Bold",
        fontSize: "16px",
        lineHeight: "150%",
        fontWeight: 400,
        color: t.textSecondary,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? KLYDO_BLACK : "#F6F5F1",
            color: t.textPrimary,
          },
          a: {
            color: isDark ? "#ffd848" : "#9A7B00",
          },
          "a:visited": {
            color: isDark ? "#e6c240" : "#806600",
          },
          "*::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.16)"
              : "rgba(0, 0, 0, 0.2)",
            borderRadius: "64px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "1000px",
            boxShadow:
              "0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              boxShadow:
                "0px 2px 8px 0px rgba(0, 0, 0, 0.15), 0px 3px 4px 0px rgba(0, 0, 0, 0.18), 0px 4px 2px -2px rgba(0, 0, 0, 0.22)",
            },
            "&.Mui-disabled": {
              pointerEvents: "auto",
              cursor: "not-allowed",
            },
          },
          sizeLarge: {
            fontSize: "16px",
            lineHeight: "24px",
            padding: "16px 40px",
            minHeight: "56px",
          },
          sizeMedium: {
            fontSize: "14px",
            lineHeight: "20px",
            padding: "10px 24px",
            minHeight: "40px",
          },
          sizeSmall: {
            fontSize: "12px",
            lineHeight: "16px",
            padding: "6px 16px",
            minHeight: "32px",
          },
          textPrimary: {
            color: isDark ? "#F7E35B" : "#8A6D00",
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
            "&.Mui-disabled": {
              color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
              backgroundColor: "transparent",
            },
          },
          textSecondary: {
            color: isDark ? "#FFFFFF" : "#0C0C0C",
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
            "&:active": {
              backgroundColor: "transparent",
            },
            "&.Mui-disabled": {
              color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
              backgroundColor: "transparent",
            },
          },
          outlinedSecondary: {
            // The secondary palette is a neutral surface color (near-white in
            // light mode, near-black in dark), so MUI's default outlined styling
            // (text + border = secondary.main) is invisible. Use readable text
            // and a standard hairline border instead.
            color: t.textPrimary,
            borderColor: isDark
              ? "rgba(255, 255, 255, 0.23)"
              : "rgba(0, 0, 0, 0.23)",
            "&:hover": {
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.4)"
                : "rgba(0, 0, 0, 0.4)",
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.04)",
            },
            "&.Mui-disabled": {
              color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.12)"
                : "rgba(0, 0, 0, 0.12)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "16px",
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.04)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.12)"
                : "rgba(0, 0, 0, 0.12)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark
                ? "rgba(255, 255, 255, 0.24)"
                : "rgba(0, 0, 0, 0.24)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: KLYDO_YELLOW,
              borderWidth: "1px",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: isDark ? "#232323" : "#FFFFFF",
            borderRadius: "24px",
          },
          paperFullScreen: {
            borderRadius: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#2E2E2E" : "#FFFFFF",
            borderRadius: "16px",
            ...(isDark
              ? {}
              : { boxShadow: "0px 8px 28px rgba(0, 0, 0, 0.16)" }),
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#2E2E2E" : "#FFFFFF",
            borderRadius: "16px",
            ...(isDark
              ? {}
              : { boxShadow: "0px 8px 28px rgba(0, 0, 0, 0.16)" }),
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#2E2E2E" : "#FFFFFF",
            borderRadius: "16px",
            ...(isDark
              ? {}
              : { boxShadow: "0px 8px 28px rgba(0, 0, 0, 0.16)" }),
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isDark
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid rgba(0, 0, 0, 0.08)",
          },
          head: {
            fontFamily: "Gilroy-SemiBold",
            color: t.textSecondary,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: "Gilroy-SemiBold",
            borderRadius: "12px",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            fontFamily: "Gilroy-SemiBold",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: isDark ? "#1A1A1A" : "#FFFFFF",
            borderRadius: "20px",
            ...(isDark ? {} : { border: "1px solid rgba(0, 0, 0, 0.06)" }),
          },
        },
      },
    },
    zIndex: {
      pendulumRod: 1,
      pendulumBall: 2,
      clockDials: 0,
      handMinute: 3,
      handMinuteS: 2,
      handHour: 3,
      handHourS: 2,
      dialog: 1,
    },
    dragAndDrop: {
      transition:
        "transform 0.5s cubic-bezier(1, 0, 0, 1), opacity 0.5s ease, border 0.2s ease",
      draggedOpacity: 0.4,
    },
  });
};
